import { TemplateRef } from '@angular/core';

import {
  type ContentPosition,
  type Positionable,
  type TextInputType,
} from '../components/textbox/textbox.component';
import { emailValidator } from '../validators';

import {
  QuestionBase,
  type IQuestionBase,
  type SetValueOptions,
} from './question-base';
import { type ISelectItem } from './question-multi-base';

export interface IQuestionTextbox<T, V = any>
  extends IQuestionBase<T>, Positionable {
  key?: string;
  type?: TextInputType;
  autocomplete?: (query: string) => Promise<ISelectItem<T, V>[]>;
  autocompleteKeep?: boolean;
  /**
   * Debounce, in ms, applied to the `autocomplete` loader: keystrokes are
   * coalesced and the loader runs at most once per idle window. Absent or `0`
   * keeps the historical behaviour — the loader fires on every keystroke.
   */
  autocompleteDebounce?: number;
  /**
   * Minimum query length before the `autocomplete` loader runs. Below it the
   * suggestions are cleared and no request is issued. Absent or `0` keeps the
   * historical behaviour — the loader fires even on an empty query.
   */
  autocompleteMinChars?: number;
  customFormatter?: (value: T) => T;
  itemTemplate?: TemplateRef<any>;
  headerTemplate?: TemplateRef<any>;
  footerTemplate?: TemplateRef<any>;
  results?: any[];
  mask?: string;
  rows?: number;
  cols?: number;
  maxHeight?: number;
  tooltip?: string;
  iconNameActionButton?: string;
  getAutocompleteLabel?: (value: T) => string;
  onFocus?: () => void;
  onBlur?: () => void;
  onClickActionButton?: (
    element: QuestionTextbox,
    value: T,
  ) => MouseEvent | void;
}

export class QuestionTextbox<T = string | number | any, V = any>
  extends QuestionBase<T>
  implements IQuestionTextbox<T, V>
{
  declare public type: TextInputType;
  public onFocus?: () => void;
  public onBlur?: () => void;
  public onClickActionButton?: (
    element: QuestionTextbox,
    value: T,
  ) => MouseEvent | void;

  public autocomplete?: (query: string) => Promise<ISelectItem<T, V>[]>;
  public autocompleteKeep?: boolean;
  public autocompleteDebounce?: number;
  public autocompleteMinChars?: number;
  public customFormatter?: (value: T) => T;
  public getAutocompleteLabel?: (value: T) => string;

  public itemTemplate?: TemplateRef<any>;
  public headerTemplate?: TemplateRef<any>;
  public footerTemplate?: TemplateRef<any>;

  public results: ISelectItem<T, V>[];
  public mask?: string;
  public rows?: number;
  public cols?: number;
  public maxHeight?: number;
  public tooltip?: string;
  public iconNameActionButton?: string | null;
  public contentPosition?: ContentPosition;

  constructor(options: IQuestionTextbox<T>) {
    super(options);
    this.elementType = 'textbox';
    this.type = options.type || 'text';

    this.onFocus = options.onFocus || (() => {});
    this.onBlur = options.onBlur || (() => {});
    this.onClickActionButton = options.onClickActionButton || (() => {});
    this.autocomplete = options.autocomplete;
    this.autocompleteKeep = options.autocompleteKeep;
    this.autocompleteDebounce = options.autocompleteDebounce;
    this.autocompleteMinChars = options.autocompleteMinChars;
    this.customFormatter = options.customFormatter;
    this.getAutocompleteLabel = options.getAutocompleteLabel;

    this.itemTemplate = options.itemTemplate;
    this.headerTemplate = options.headerTemplate;
    this.footerTemplate = options.footerTemplate;
    this.results = [];

    this.mask = options.mask;
    this.placeholder = options.placeholder;

    this.tooltip = options.tooltip || '';
    this.iconNameActionButton = options.iconNameActionButton || null;

    this.contentPosition = options.contentPosition || 'before';

    this._formatValue ??= this._defaultFormatValue;

    switch (this.type) {
      case 'textarea': {
        this.rows = options.rows;
        this.cols = options.cols;
        this.maxHeight = options.maxHeight;

        break;
      }
      case 'email': {
        this.validators.push(emailValidator());

        break;
      }
      default: {
        break;
      }
    }
  }

  public override setValue(
    value: T,
    opts: SetValueOptions = { emitEvent: true, force: false },
  ): void {
    return super.setValue(this._formatValue(value), opts);
  }

  public override getValue(): T {
    return this._formatValue(super.getValue());
  }

  public override equals(value: any): boolean {
    return this.getValue() === this._formatValue(value);
  }

  public getAutocompleteValue(): ISelectItem<T, V> {
    const value = this.getValue();
    const label = this.getAutocompleteLabel
      ? this.getAutocompleteLabel(this.getValue())
      : '';

    return {
      value,
      label,
    };
  }

  private _defaultFormatValue(value: T): T {
    if (value === undefined) {
      return;
    }

    if (typeof value === 'object') {
      return value;
    }

    if (String(value).trim() === '' || value === null) {
      return null;
    }

    if (this.customFormatter) {
      return this.customFormatter(value);
    }

    return String(value).trim() as unknown as T;
  }
}
